/**
 * Shared outcome→text/icon mappings — used by both the step log (per-step
 * detail) and anywhere else an outcome needs to render as headline text or
 * supplementary badges. Pulled out of OutcomePanel.tsx so StepLog.tsx isn't
 * reaching into a component file for plain data.
 */
import type { ChemistryLabOutcome } from './types';
import { resolveColorHex } from './colorMapping';
import { pHBandToHex } from './phPaperColor';
import { isBoiling } from './temperature';

export const PH_LABELS: Record<string, string> = {
  strongly_acidic: 'Strongly acidic', acidic: 'Acidic', neutral: 'Neutral',
  basic: 'Basic', strongly_basic: 'Strongly basic',
};
export const GAS_LABELS: Record<string, string> = {
  hydrogen: 'H₂ gas', carbon_dioxide: 'CO₂ gas', sulfur_dioxide: 'SO₂ gas',
  nitrogen_dioxide: 'NO₂ gas', nitric_oxide: 'NO gas', hydrogen_sulfide: 'H₂S gas',
  ammonia: 'NH₃ gas', hydrogen_chloride: 'HCl gas', chlorine: 'Cl₂ gas', other: 'Gas evolved',
};

/** Whether a color description is exactly "colorless"/"colourless" — no
 *  visible tint to show. Used to skip a color_change headline that has
 *  nothing at either end worth a swatch or a sentence. */
function isColorless(description: string): boolean {
  return /^\s*colou?rless\s*$/i.test(description);
}

/** Fumes render only when the outcome names a visible gas color — LLM-
 *  supplied per mix, the same way precipitate.color is, rather than a fixed
 *  per-gas-id lookup here. A colorless gas (H2, CO2, ...) reports
 *  `color: null` and stays bubbles-only — as does one where the model wrote
 *  the word "colorless" as text instead of using null; nothing to maintain
 *  client-side as new visibly-colored gases come up. */
export function fumeColorFor(gasEvolution: ChemistryLabOutcome['gasEvolution']): string | null {
  const color = gasEvolution?.color;
  if (!color || isColorless(color)) return null;
  return resolveColorHex(gasEvolution!.colorHex, color);
}

export interface HeadlineItem {
  icon?: string;
  swatch?: string;
  text: string;
}

/** The facts a student should see before reading the sentence — a step with
 *  none of these (e.g. adding water to an empty container) has nothing
 *  notable to call out, and the step log renders just its label.
 *
 *  `celsius` is the burner setting the outcome was computed at — optional
 *  because not every caller has it (a bare outcome, e.g. in a context with no
 *  live temperature, still renders its chemistry-only headline). When given,
 *  it adds "Boiling" whenever that temperature has crossed the mixture's own
 *  boiling point — a fact the outcome can't state on its own, since whether
 *  the CONTENTS boil depends on comparing `boilingPointC` against the burner,
 *  not anything the reaction itself produced (see temperature.ts). */
export function headlineItems(outcome: ChemistryLabOutcome, celsius?: number): HeadlineItem[] {
  const items: HeadlineItem[] = [];
  // Color leads the list — right under the equation (see the reaction
  // card's own layout in ChemistryLabScene2D) — since it's the one fact a
  // student can check with their own eyes against the vessel in front of
  // them, before reading anything else the mix produced.
  if (outcome.colorChange) {
    const { from, to } = outcome.colorChange;
    // "colorless -> colorless" says nothing a reader doesn't already know
    // from the empty vessel in front of them — worth a line only when there
    // is an actual color at either end (a real change, or a decolorization
    // like "blue -> colorless" is still news; that's what stays visible).
    // The same is true of ANY "X -> X" — not just colorless ones. A heavy
    // precipitate case in particular sometimes reports the SOLUTION as
    // unchanged (e.g. "pale green -> pale green") right next to a
    // precipitate the model describes with a genuinely different colour
    // (e.g. "greenish-white") — the no-op liquid line next to a different
    // solid colour reads as contradictory, when it's really just two
    // different things (liquid vs. settled solid) that never had to match.
    // Dropping the no-op line leaves only the one color fact that changed.
    const noChange = from.trim().toLowerCase() === to.trim().toLowerCase();
    if (!noChange && !(isColorless(from) && isColorless(to))) {
      items.push(isColorless(to)
        ? { icon: '🎨', text: `${from} → ${to}` }
        : { swatch: resolveColorHex(outcome.colorChange.toHex, to), text: `${from} → ${to}` });
    }
  }
  if (outcome.temperatureChange && outcome.temperatureChange.direction !== 'none') {
    items.push({
      icon: outcome.temperatureChange.direction === 'exothermic' ? '🔥' : '❄️',
      text: outcome.temperatureChange.direction === 'exothermic' ? 'Exothermic Reaction' : 'Endothermic Reaction',
    });
  }
  if (outcome.precipitate && outcome.precipitate.amount !== 'none') {
    const { color, amount } = outcome.precipitate;
    const capitalColor = `${color.charAt(0).toUpperCase()}${color.slice(1)}`;
    // A vapor-phase outcome (see types.ts's `phase` doc) reuses this SAME
    // field for the visible smoke/fumes two gases produce meeting in open
    // air — "White Precipitate" is the wrong word for something that never
    // touched a liquid to settle out of; "Dense/Faint ... Fumes" is what it
    // actually is.
    const text = outcome.phase === 'vapor'
      ? `${amount === 'heavy' ? 'Dense' : 'Faint'} ${capitalColor} Fumes`
      : `${capitalColor} Precipitate`;
    items.push({ swatch: resolveColorHex(outcome.precipitate.colorHex, color), text });
  }
  if (outcome.gasEvolution) {
    // "Gas evolved" alone said nothing a student couldn't already see from
    // the equation's own Cl2/NO2/etc — the fact worth surfacing HERE is what
    // it actually looks like escaping the vessel, the same way the
    // precipitate item below shows a swatch rather than just "Precipitate".
    // null for a colorless gas (H2, CO2, ...), so those still read as plain
    // label-only, matching fumeColorFor's own null-means-no-visible-fumes
    // rule — including when the model wrote "colorless" as text instead of
    // using null, same as the color_change case above.
    const label = GAS_LABELS[outcome.gasEvolution.gas] ?? 'Gas Evolved';
    const gasColor = outcome.gasEvolution.color;
    items.push(gasColor && !isColorless(gasColor)
      ? { swatch: resolveColorHex(outcome.gasEvolution.colorHex, gasColor), text: `${label} (${gasColor})` }
      : { icon: '💨', text: label });
  }
  if (celsius !== undefined && isBoiling(celsius, outcome.boilingPointC)) {
    items.push({ icon: '♨️', text: 'Boiling' });
  }
  return items;
}

/** Whether this outcome is worth calling out at all — a plain "added water"
 *  single-substance result with no color/gas/heat/precipitate change has
 *  nothing to say beyond the step label itself. */
export function isNotableOutcome(outcome: ChemistryLabOutcome, celsius?: number): boolean {
  return headlineItems(outcome, celsius).length > 0;
}

/**
 * What a TEST step found. Dipping the pH paper is a measurement, so its
 * result is the reading — not the mixture's standing description, which the
 * step before it already gave. Always reported, even when unchanged: "still
 * strongly acidic" is the answer to the question that was asked.
 */
export function testItems(outcome: ChemistryLabOutcome): HeadlineItem[] {
  const items: HeadlineItem[] = [];
  if (outcome.pHBand) items.push({ swatch: pHBandToHex(outcome.pHBand), text: PH_LABELS[outcome.pHBand] });
  return items;
}

/**
 * What an ACTION step actually changed — the facts that weren't already true
 * before it. A step that repeats "Exothermic Reaction, Gas evolved" under
 * every subsequent action is describing the beaker, not the step, and buries
 * the one line that is genuinely news.
 */
export function changedItems(
  outcome: ChemistryLabOutcome, previous: ChemistryLabOutcome | undefined,
  celsius?: number, previousCelsius?: number,
): HeadlineItem[] {
  const before = new Set(previous ? headlineItems(previous, previousCelsius).map(item => item.text) : []);
  return headlineItems(outcome, celsius).filter(item => !before.has(item.text));
}
